import axios from 'axios';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/core/Select';
import { useDataModel } from '@/hooks/useDataModel';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { restUrl } from '@/services/utils';

import { Button } from '../core/Button';
import { Textarea } from '../core/Textarea';
import { AttributeComponent } from '../datamodel/AttributeComponent';
import { LayoutProvider } from '../datamodel/hooks/useLayout';

function ReviewPanel({ primaryKey }: any) {

    const reviewStates = ['APPROVED', 'REVIEWED', 'REJECTED', 'RECEIVED'];

    const { t } = useTranslation();
    const {toast} = useToast();
    const { dataModel } = useDataModel();
    const formMethods = useForm();

    const onSubmit = async (data: any) => {
        const customValues: any = {};

        dataModel?.getReviewAttributes().forEach((attribute: any) => {
            if (data[attribute.name]) {
                customValues[attribute.name] = data[attribute.name];
            }
        });

        const review = {
            reviewStatus: data.reviewStatus,
            customValues,
            comment: data.reviewComment,
            primaryKey: primaryKey,
        };

        await axios.post<void>(restUrl('reviewerreviews'), review)
            .then(_response => {
                toast.success('SAVED_REVIEW_SUCCESS_MESSAGE');
                formMethods.reset();
            })
            .catch(_error => {
                toast.error('SAVED_REVIEW_ERROR_MESSAGE');
            });
    };

    return (
        <div className='tw-w-full'>
            <FormProvider {...formMethods}>
                <form className='tw-overflow-x-hidden' onSubmit={formMethods.handleSubmit(onSubmit)}>
                    <LayoutProvider layoutName='edit'>
                        <div className='tw-w-full tw-pb-3'> {t('ITEM_REVIEW_CREATE')} </div>

                        {/* Review Status Select */}
                        <div id='review_reviewStatus'>
                            <Controller
                                name="reviewStatus"
                                control={formMethods.control}
                                defaultValue={null}
                                render={({ field }) => (
                                    <Select value={field.value} onValueChange={field.onChange}>
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {reviewStates.map((option: string, index: number) => (
                                                <SelectItem key={index} value={option}>
                                                    {t(`REVIEW.${option}`)}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                )}
                            />
                        </div>

                        {/* Dynamic review attributes */}
                        {dataModel?.getReviewAttributes().map((attribute: any, index: number) => (
                            <div key={index}>
                                <div id={`review_${attribute.name}`}>
                                    <AttributeComponent
                                        key= {attribute.name}
                                        attribute={attribute}
                                        direction='vertical'
                                    />
                                </div>
                            </div>
                        ))}

                        {/* Review Comment Textarea */}
                        <div className='tw-pb-4'>
                            <label className='tw-pb-4 tw-text-t-sm'>{t('REVIEW.COMMENT')}</label>
                            <Controller
                                name="reviewComment"
                                control={formMethods.control}
                                render={({ field }) => <Textarea {...field} />}
                            />
                        </div>

                        <Button variant='outline' type='submit'>{t('REVIEW.SEND_REVIEW')}</Button>
                    </LayoutProvider>
                </form>
            </FormProvider>
        </div>
    );
}

export default ReviewPanel;
